import { ConnectCall, Role } from "@ameelio/connect-call-client";
import { Avatar, IconButton } from "@ameelio/ui";
import CloseIcon from "@mui/icons-material/Close";
import LockOutlined from "@mui/icons-material/LockOutlined";
import RemoveRedEyeOutlined from "@mui/icons-material/RemoveRedEyeOutlined";
import { Alert, Box, Grow, Stack, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { CallFeature, CallParticipant } from "../../api/graphql";
import { isMonitoredCall } from "../../lib/securityFeaturesUtils.ts";
import useScrolledToBottom from "../../lib/useScrolledToBottom";
import AlertMessage from "./AlertMessage.tsx";
import ChatInput, { Props as ChatInputProps } from "./ChatInput.tsx";
import ChatMessage from "./ChatMessage.tsx";

type Props = Pick<ChatInputProps, "onSend" | "muteSounds"> & {
  facilityName: string;
  callFeatures: CallFeature[];
  currentParticipantId: string;
  participants: Pick<CallParticipant, "externalId" | "fullName">[];
  messages: ConnectCall["messages"];
  onClose: () => void;
};

export default function CallChat({
  facilityName,
  currentParticipantId,
  participants,
  callFeatures,
  messages,
  onSend,
  onClose,
  muteSounds,
}: Props) {
  const { t } = useTranslation();

  const { scrolledToBottom, onScroll } = useScrolledToBottom(true);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: 1 }}>
      <Box sx={{ display: "flex", padding: 2 }}>
        <Typography variant="h3" component="div" sx={{ flexGrow: 1 }}>
          {t("In-call chat")}
        </Typography>
        <IconButton size="small" ariaLabel={t("Close")} onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </Box>
      <Box
        sx={{
          flexGrow: 1,
          overflowY: "scroll",
          px: 2,
        }}
        onScroll={onScroll}
      >
        {isMonitoredCall(callFeatures) && (
          <Alert
            severity="info"
            icon={<RemoveRedEyeOutlined />}
            sx={{ justifyContent: "center" }}
          >
            {onSend
              ? t(
                  "Messages can be seen by security staff. They will not be accessible to you when the call ends."
                )
              : t("Messages will be available once the call is live")}
          </Alert>
        )}
        {!isMonitoredCall(callFeatures) && (
          <Alert
            severity="info"
            icon={<LockOutlined />}
            sx={{ justifyContent: "center" }}
          >
            {onSend
              ? t(
                  "Messages cannot be seen by security staff. They will not be accessible to you when the call ends."
                )
              : t("Messages will be available once the call is live")}
          </Alert>
        )}
        <Stack
          sx={{
            mt: 2,
            gap: 1,
          }}
        >
          {messages.map((m) => {
            // lookups are risky. i don't know how to avoid this one yet.
            const participant = participants.find(
              (p) => p.externalId === m.user.id
            ) || {
              externalId: "",
              fullName: "",
            };
            return (
              <Grow
                in
                timeout={500}
                style={{ transformOrigin: "bottom center" }}
                key={m.timestamp.getTime()}
              >
                <Box
                  sx={[
                    {
                      display: "flex",
                    },
                    m.user.role === Role.monitor
                      ? {}
                      : {
                          [currentParticipantId === participant.externalId
                            ? "ml"
                            : "mr"]: 2,
                          justifyContent:
                            currentParticipantId === participant.externalId
                              ? "flex-end"
                              : "flex-start",
                        },
                  ]}
                >
                  {m.user.role === Role.monitor ? (
                    <AlertMessage
                      facilityName={facilityName}
                      contents={m.contents}
                      timestamp={m.timestamp}
                    />
                  ) : (
                    <ChatMessage
                      currentUserId={currentParticipantId}
                      correspondent={{
                        id: participant.externalId,
                        fullName: participant.fullName,
                      }}
                      contents={m.contents}
                      timestamp={m.timestamp}
                      iconSlot={
                        <Avatar
                          contact={participant}
                          sx={{ fontSize: 12, height: 20, width: 20 }}
                        />
                      }
                    />
                  )}
                </Box>
              </Grow>
            );
          })}
        </Stack>
        {scrolledToBottom && (
          <div
            ref={(node) =>
              node?.scrollIntoView({
                behavior: "auto",
                block: "start",
              })
            }
          />
        )}
      </Box>
      <Box sx={{ padding: 2 }}>
        <ChatInput
          label={t("Message")}
          onSend={onSend}
          muteSounds={muteSounds}
        />
      </Box>
    </Box>
  );
}
